// RDF Types
//
// These alias the RDF/JS community types rather than redeclaring a subset.
// The hand-rolled versions they replace predated RDF 1.2 and silently excluded
// two term kinds the parser already produces: triple terms (termType 'Quad',
// valid only as an object) and literals with an initial text direction. The
// parser hands n3 term objects straight through, so this is what the values
// really are at runtime.
import type * as RDF from '@rdfjs/types'

export type NamedNode = RDF.NamedNode
export type BlankNode = RDF.BlankNode
export type Literal = RDF.Literal
/** RDF 1.2 triple term. Only ever appears in the object position. */
export type TripleTerm = RDF.Quad

export type RDFTerm = RDF.Term

export type RDFQuad = RDF.Quad

export interface RDFNode {
  termType: 'NamedNode' | 'BlankNode' | 'Literal'
  value: string
  datatype?: NamedNode
  language?: string
}

export interface RDFPrefix {
  prefix: string
  namespace: string
}

// Editor Types
export type EditorLanguage = 'turtle' | 'xml' | 'javascript' | 'dot' | 'sparql'
export type EditorTheme = 'light' | 'dark' | 'high-contrast'

/**
 * One document open in the editor pane. The first tab holds whatever the user
 * loaded or typed; further tabs hold graphs returned by CONSTRUCT / DESCRIBE
 * queries. Each tab owns its text, its language and the subjects picked for
 * the diagram, so switching or closing tabs never touches another tab's data.
 */
export interface EditorTab {
  id: string
  title: string
  content: string
  language: EditorLanguage
  /** Diagram selection while this tab is active; restored when it is again. */
  selectedSubjects: string[]
}

export interface EditorState {
  /**
   * Text and language of the active tab. The reducer keeps these in step
   * with `tabs`, so the many readers of `editor.content` need not know about
   * tabs at all.
   */
  content: string
  language: EditorLanguage
  theme: EditorTheme
  fontSize: number
  isLoading: boolean
  tabs: EditorTab[]
  activeTabId: string
  /** How many result tabs have been opened, for naming the next one. */
  resultCount: number
}

// Graph Types
export type GraphEngine = 'circo' | 'dot' | 'fdp' | 'neato' | 'osage' | 'twopi'
export type GraphFormat = 'svg' | 'png' | 'json' | 'xdot' | 'plain' | 'ps'
export type LayoutDirection = 'LR' | 'RL' | 'TB' | 'BT'

export interface GraphOptions {
  engine: GraphEngine
  format: GraphFormat
  layoutDirection: LayoutDirection
  showPrefixes: boolean
  hideTypes: boolean
  hideAnnotations: boolean
  showSubjects: boolean
  rawOutput: boolean
  sortSubjects: boolean
  /** Show rdfs:label / skos:prefLabel instead of the raw IRI in the subject picker. */
  showLabels: boolean
  /** Same, for the node boxes in the rendered diagram. */
  showNodeLabels: boolean
  /** Same, for the property names on the diagram's edges. */
  showPredicateLabels: boolean
  /** Draw dashed links from each triple-term node to the subject and object it mentions. */
  linkTripleTerms: boolean
}

export interface GraphState {
  dotText: string
  svgOutput: string
  options: GraphOptions
  isGenerating: boolean
  error: string | null
}

// RDF Store Types
export interface RDFStore {
  quads: RDFQuad[]
  subjects: string[]
  prefixes: Record<string, string>
  selectedSubjects: string[]
  /** Subject key -> human-readable label, for subjects that have one. */
  labels: Record<string, string>
}

// SPARQL Types
export interface SPARQLQuery {
  query: string
  results?: any
  isExecuting: boolean
  error?: string
  /** Open the graph a CONSTRUCT / DESCRIBE returns in a new editor tab. */
  openResultsInTab: boolean
}

// File Types
export interface FileOperation {
  type: 'load' | 'save' | 'url-load'
  filename?: string
  url?: string
  content?: string
}

// App State
// SHACL Types
export type ShaclSeverity = 'Violation' | 'Warning' | 'Info'

/** One validation result, with terms left as the engine reports them. */
export interface ShaclResult {
  severity: ShaclSeverity
  focusNode: string
  /**
   * Whether the focus node is a blank node, in which case `focusNode` is the
   * engine's own label for it and names nothing the reader can look up. A
   * focus node is only ever an IRI or a blank node, so this is readable from
   * the value; a literal, which could begin `_:` as its text, cannot be one.
   */
  focusIsBlank: boolean
  /** The property path as an IRI or a rendered path expression; null for node-level results. */
  path: string | null
  /** The offending value; null where the constraint is about absence (sh:minCount, sh:closed). */
  value: string | null
  message: string
  /** The shape the result came from: an IRI, or "<parent> › property N" for a nested property shape. */
  sourceShape: string | null
  /** The constraint component IRI, e.g. sh:MinCountConstraintComponent. */
  component: string
}

/**
 * What the engine materialises before validating. `none` is SHACL as the
 * specification defines it; `rdfs` adds the RDFS closure of the data; `rules`
 * runs SHACL-AF rules (sh:rule) once, as SHACL-AF defines; `rules-iterated`
 * repeats them to a fixpoint, which the specification leaves undefined.
 */
export type ShaclInference = 'none' | 'rdfs' | 'rules' | 'rules-iterated'

export const SHACL_INFERENCE_MODES: readonly ShaclInference[] = ['none', 'rdfs', 'rules', 'rules-iterated']

export interface ShaclReport {
  conforms: boolean
  /** The inference mode the report was produced under. */
  inference: ShaclInference
  results: ShaclResult[]
  counts: Record<ShaclSeverity, number>
  shapeCount: number
  /** The full validation report graph as Turtle, for opening as a tab or saving. */
  turtle: string
  /** Prefixes for display, merged from the data and the shapes. */
  prefixes: Record<string, string>
}

export interface ShaclState {
  /** The editor tab holding the shapes graph. */
  shapesTabId?: string
  /** What to materialise before validating; see ShaclInference. */
  inference: ShaclInference
  report?: ShaclReport
  isValidating: boolean
  error?: string
}

// Query plan types
/** One operator in the plan, with the statistics from the run that measured it. */
export interface PlanNode {
  /** Stable within one plan; the node's position in the tree. */
  id: string
  /** The operator as the engine names it, arguments included. */
  name: string
  /** The name up to its first bracket: QuadPattern, LeftJoin, Filter, Project. */
  kind: string
  /** Whatever was inside the brackets. */
  detail: string
  rows: number
  seconds: number
  children: PlanNode[]
}

export interface QueryPlan {
  planningSeconds: number
  /** What the whole plan took: the root operator's own duration. */
  runSeconds: number
  /** Rows the query returned. */
  rows: number
  nodeCount: number
  root: PlanNode
}

export interface QueryPlanState {
  plan?: QueryPlan
  /** The plan drawn as DOT; shown in the diagram pane in place of the data. */
  dotText: string
  isExplaining: boolean
  error?: string
}

export interface AppState {
  editor: EditorState
  graph: GraphState
  rdf: RDFStore
  sparql: SPARQLQuery
  shacl: ShaclState
  queryPlan: QueryPlanState
}

// Component Props
export interface ComponentProps {
  className?: string
  children?: React.ReactNode
}

// Utility Types
export interface ErrorState {
  message: string
  details?: string
}

export interface LoadingState {
  isLoading: boolean
  message?: string
}

// Viz.js Types
export interface VizOptions {
  engine: string
  format: string
}

export interface VizResult {
  data: string
  errors?: string[]
}

// URL Parameter Types
export interface URLParams {
  dot?: string
  rdfa?: string
}